import { CellContext, Table as ReactTable, Row } from '@tanstack/react-table';
import { NoddiIcon } from 'noddi-ui-common';
import {
  DEFAULT_CURRENCY,
  formatClockTime,
  formatCurrencyAmount,
  formatPhoneNumber,
  type MaybeDate,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';
import { useMemo } from 'react';
import { NoddiCheckbox } from '../../Inputs/NoddiCheckbox';
import { NavioDateTimeCell } from '../../Other/NavioDateTimeCell';
import { PrettySlug } from '../../Other/PrettySlug';
import { NoddiColumnDef, TableActionType } from './types';

export function getActionTypeIcon(actionType: TableActionType) {
  switch (actionType) {
    case 'edit':
      return <NoddiIcon name='Edit' />;
    case 'delete':
      return <NoddiIcon name='Trash2' />;
    case 'view':
      return <NoddiIcon name='Eye' />;
    case 'copy':
      return <NoddiIcon name='Copy' />;
    case 'add':
      return <NoddiIcon name='Plus' />;
    case 'remove':
      return <NoddiIcon name='Minus' />;
    case 'swap':
      return <NoddiIcon name='ArrowLeftRight' />;
    case 'print':
      return <NoddiIcon name='Printer' />;
    case 'undo':
      return <NoddiIcon name='Undo2' />;
    default:
      return null;
  }
}

export function useGetProcessedColumns<TData, TValue>({
  columns,
  enableRowSelection
}: {
  columns: NoddiColumnDef<TData, TValue>[];
  enableRowSelection: boolean;
}) {
  // Wrap columns with meta.type === 'date' | 'date-time' | 'time' | 'currency' | 'phone' | 'slug'
  const columnsWithFormatting: NoddiColumnDef<TData, TValue>[] = useMemo(() => {
    return columns.map((col) => {
      // Only wrap if meta.type is set and no custom cell renderer
      if (col.meta?.type && !col.cell) {
        if (col.meta.type === 'date') {
          return {
            ...col,
            cell: (cell: CellContext<TData, TValue>) => {
              const value = cell.getValue();
              return value ? (
                <div className='whitespace-nowrap'>{noddiFormatDate(value as string, NoddiDateFormats.DATE)}</div>
              ) : null;
            }
          };
        }
        if (col.meta.type === 'date-time') {
          return {
            ...col,
            cell: (cell: CellContext<TData, TValue>) => <NavioDateTimeCell value={cell.getValue() as MaybeDate} />
          };
        }
        if (col.meta.type === 'time') {
          return {
            ...col,
            cell: (cell: CellContext<TData, TValue>) => {
              const value = cell.getValue();
              return typeof value === 'string' && value ? (
                <div className='whitespace-nowrap'>{formatClockTime(value)}</div>
              ) : null;
            }
          };
        }
        if (col.meta.type === 'currency') {
          return {
            ...col,
            cell: (cell: CellContext<TData, TValue>) => {
              const value = cell.getValue();
              return (
                <div className='whitespace-nowrap'>
                  {value !== null && value !== undefined
                    ? formatCurrencyAmount({
                        money: {
                          amount: value as number,
                          currency: DEFAULT_CURRENCY
                        }
                      })
                    : ''}
                </div>
              );
            }
          };
        }
        if (col.meta.type === 'phone') {
          return {
            ...col,
            cell: (cell: CellContext<TData, TValue>) => {
              const value = cell.getValue();
              return <div className='whitespace-nowrap'>{value ? formatPhoneNumber(value as string) : ''}</div>;
            }
          };
        }
        if (col.meta.type === 'slug') {
          return {
            ...col,
            cell: (cell: CellContext<TData, TValue>) => {
              const value = cell.getValue();
              return typeof value === 'string' && value ? <PrettySlug slug={value} /> : null;
            }
          };
        }
      }
      return col;
    });
  }, [columns]);

  const processedColumns = useMemo(() => {
    return [
      ...(enableRowSelection
        ? [
            {
              id: 'select',
              size: 48,
              header: ({ table }: { table: ReactTable<TData> }) => (
                // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- wrapper that stops a click on the select-all checkbox from bubbling to the table header
                <div onClick={(e) => e.stopPropagation()}>
                  <NoddiCheckbox
                    checked={table.getIsAllPageRowsSelected()}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label='Select all'
                  />
                </div>
              ),
              cell: ({ row }: { row: Row<TData> }) => (
                // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- wrapper that stops a click on the row checkbox from bubbling to the row's own click handler
                <div onClick={(e) => e.stopPropagation()}>
                  <NoddiCheckbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onCheckedChange={row.getToggleSelectedHandler()}
                  />
                </div>
              ),
              enableSorting: false,
              enableHiding: false
            }
          ]
        : []),
      ...columnsWithFormatting
    ];
  }, [columnsWithFormatting, enableRowSelection]);

  return {
    processedColumns
  };
}
